import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { getVenueById, updateVenueById, updateUserProfile, getUserProfile } from "../../../../api";
import { Spinner } from "reactstrap";

const validationSchema = Yup.object({
  name: Yup.string(),
  capacity: Yup.number().nullable(),
phone: Yup.string()
  .trim()
  .nullable()
  .transform((v) => (v === "" ? null : v))
  .matches(/^\+?[0-9\s\-()]{6,20}$/, {
    message: "Enter a valid phone number",
    excludeEmptyString: true,
  }),
  email: Yup.string().email("Invalid email").nullable(),
  website: Yup.string().url("Invalid URL").nullable(),
  about: Yup.string().nullable(),
  venueAddress: Yup.object({
    name: Yup.string().nullable(),
  }),
});

const VenueDetailsTab = ({ venueData, venueId, refreshVenue }: any) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [initialValues, setInitialValues] = useState<any>({
    name: "",
    capacity: "",
    phone: "",
    email: "",
    website: "",
    about: "",
    venueAddress: {
      name: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      latitude: undefined,
      longitude: undefined,
    },
  });

  useEffect(() => {
    if (!venueData) return;
    // Prefer venue row fields; fall back to nested venueUser / user profile fields.
    setInitialValues({
      name: venueData.name ?? "",
      capacity: venueData.capacity ?? "",
      website: venueData.website ?? "",
      about: venueData.about ?? "",
      email:
        venueData.email ??
        venueData.venueUser?.email ??
        "",
      phone:
        venueData.phone ??
        venueData.venueUser?.mobileNumber ??
        venueData.venueUser?.phone ??
        "",
      venueAddress: {
        name: venueData.address_name ?? "",
        addressLine1: venueData.address ?? "",
        addressLine2: venueData.address_line_2 ?? "",
        city: venueData.city ?? "",
        state: venueData.state ?? "",
        zipCode: venueData.zipcode ?? "",
        country: venueData.country?.countryName ?? venueData.country_name ?? "",
        latitude: venueData.latitude
          ? Number(venueData.latitude)
          : undefined,
        longitude: venueData.longitude
          ? Number(venueData.longitude)
          : undefined,
      },
    });
    
    const fetchProfile = async () => {
      const stored = localStorage.getItem("venueData");
      const userId = stored
        ? JSON.parse(stored).userId || JSON.parse(stored).id
        : venueData.venueUser?.venueUserId || venueData.venue_user_id;
      if (userId) {
        try {
          setIsProfileLoading(true);
          const profile = await getUserProfile(userId);
          setPreviewUrl(profile?.profileImage ?? venueData.venueUser?.profileImage ?? venueData.venue_image ?? null);
        } catch (err) {
          console.error("Failed to fetch user profile", err);
          setPreviewUrl(venueData.venueUser?.profileImage ?? venueData.venue_image ?? null);
        } finally {
          setIsProfileLoading(false);
        }
      } else {
        setPreviewUrl(venueData.venueUser?.profileImage ?? venueData.venue_image ?? null);
      }
    };
    fetchProfile();
  }, [venueData, venueId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      setProfileFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setProfileFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };


  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (isSaving) return;
      try {
        setIsSaving(true);

        if (!venueId) {
          toast.error("Venue not loaded yet. Please refresh and try again.");
          return;
        }

        const payload = {
          name: values.name,
          capacity: values.capacity ? Number(values.capacity) : null,
          phone: values.phone || null,
          email: values.email || null,
          website: values.website || null,
          about: values.about || null,
        
          venueAddress: {
            name: values.venueAddress?.name || null,
            addressLine1: values.venueAddress?.addressLine1 || null,
            addressLine2: values.venueAddress?.addressLine2 || "",
            city: values.venueAddress?.city || "",
            state: values.venueAddress?.state || "",
            country: values.venueAddress?.country || "",
            zipCode: values.venueAddress?.zipCode || "",
            latitude: values.venueAddress?.latitude ?? 0,
            longitude: values.venueAddress?.longitude ?? 0,
          }
        };
        
        await updateVenueById(venueId, payload);
        
        if (profileFile) {
          const stored = localStorage.getItem("venueData");
          const userId = stored ? JSON.parse(stored).userId : venueData?.venueUser?.id;
          if (userId) {
            const formData = new FormData();
            formData.append("profileImage", profileFile);
            await updateUserProfile(userId, formData);
          }
        }

        toast.success("Venue details updated");
        await refreshVenue();
      } catch (err) {
        toast.error("Update failed");
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <div className="tab-from-content">
      <ToastContainer autoClose={2000} />
      <div className="venue-tab-card main-card">
        <h4 className="venue-tab-title">
          <i className="fa-solid fa-circle-info" />
          Venue Details
        </h4>
        <div className="venue-tab-body">
          <form onSubmit={formik.handleSubmit}>
            <div className="d-flex align-items-center gap-4 mb-4 pb-4 border_bottom">
              <div className="position-relative" style={{ width: "100px", height: "100px", flexShrink: 0 }}>
                <div
                  className="rounded-circle overflow-hidden border border-2 d-flex align-items-center justify-content-center bg-light"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderColor: "#ee3f7c",
                    cursor: "pointer",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.05)"
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isProfileLoading ? (
                    <Spinner style={{ color: "#ee3f7c" }} />
                  ) : previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Venue Logo"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <i className="fa-solid fa-hotel text-muted fa-2x" />
                  )}
                </div>
                <button
                  type="button"
                  className="position-absolute bottom-0 end-0 btn btn-sm rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#ee3f7c",
                    border: "2px solid #fff",
                    padding: 0,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Upload profile image"
                >
                  <i className="fa-solid fa-camera fa-xs" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/jpeg,image/png"
                  onChange={handleImageChange}
                />
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1" style={{ fontSize: "16px", fontWeight: "600" }}>Venue Profile Image</h5>
                <p className="text-muted small mb-1">
                  Upload a logo or cover image for your venue profile.
                </p>
              </div>
            </div>

            <div className="venue-form-section form-group">
              <label className="form-label">Venue Name</label>
              <input
                className="form-control h_50"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
              />
            </div>

            <div className="venue-form-section col-md-12">
              <div className="google-places-container">
                <label className="form-label">
                  <i className="fa-solid fa-map-marker-alt me-2" />
                  Event Location *
                </label>

                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                  selectProps={{
                    placeholder: "Search for a location or address...",
                    isClearable: true,

                    value: formik.values.venueAddress.name
                      ? {
                          label: formik.values.venueAddress.name,
                          value: {
                            description: formik.values.venueAddress.addressLine1,
                            place_id: formik.values.venueAddress.name,
                            geometry: {
                              location: {
                                lat: formik.values.venueAddress.latitude,
                                lng: formik.values.venueAddress.longitude,
                              },
                            },
                          },
                        }
                      : null,

                    onChange: (place: any) => {
                      if (!place) {
                        formik.setFieldValue("venueAddress", {
                          name: "",
                          formattedAddress: "",
                          addressLine1: "",
                          addressLine2: "",
                          country: "",
                          state: "",
                          city: "",
                          zipCode: "",
                          latitude: undefined,
                          longitude: undefined,
                        });
                        return;
                      }

                      const baseDetails = {
                        name: place.label || "",
                        formattedAddress: place.label || "",
                        addressLine1: place.value?.description || "",
                        addressLine2: "",
                        country: "",
                        state: "",
                        city: "",
                        zipCode: "",
                        latitude: place.value?.geometry?.location?.lat,
                        longitude: place.value?.geometry?.location?.lng,
                      };

                      if (!place.value?.place_id) {
                        formik.setFieldValue("venueAddress", baseDetails);
                        return;
                      }

                      const geocoder = new google.maps.Geocoder();
                      geocoder.geocode(
                        { placeId: place.value.place_id },
                        (results, status) => {
                          if (status !== "OK" || !results?.[0]) {
                            formik.setFieldValue("venueAddress", baseDetails);
                            return;
                          }

                          const result = results[0];
                          const enhanced = {
                            ...baseDetails,
                            formattedAddress: result.formatted_address,
                            addressLine1: result.formatted_address,
                            latitude: result.geometry.location.lat(),
                            longitude: result.geometry.location.lng(),
                          };

                          result.address_components.forEach((component) => {
                            const types = component.types;
                            if (types.includes("country"))
                              enhanced.country = component.long_name;
                            if (types.includes("administrative_area_level_1"))
                              enhanced.state = component.long_name;
                            if (types.includes("locality"))
                              enhanced.city = component.long_name;
                            if (types.includes("postal_code"))
                              enhanced.zipCode = component.long_name;
                          });

                          formik.setFieldValue("venueAddress", enhanced);
                        }
                      );
                    },

                    styles: {
                      control: (provided: any, state: any) => ({
                        ...provided,
                        minHeight: "48px",
                        backgroundColor: "transparent", 
                        color: "var(--gp-text, #495057)",             
                        border: state.isFocused
                          ? "2px solid #ee3f7c"
                          : "1px solid var(--gp-border, #ced4da)",
                        borderRadius: "8px",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.2rem rgba(238, 63, 124, 0.15)"
                          : "none",
                        "&:hover": {
                          borderColor: "#ee3f7c",
                        },
                      }),
                      placeholder: (provided: any) => ({
                        ...provided,
                        color: "var(--gp-placeholder, #6c757d)",
                        opacity: 0.8,
                        fontSize: "14px",
                      }),
                      input: (provided: any) => ({
                        ...provided,
                        fontSize: "14px",
                        padding: "8px 0",
                        color: "var(--gp-text, #495057)", 
                      }),
                      menu: (provided: any) => ({
                        ...provided,
                        backgroundColor: "var(--gp-menu-bg, white)",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        border: "1px solid var(--gp-menu-border, #e9ecef)",
                      }),
                      option: (provided: any, state: any) => ({
                        ...provided,
                        backgroundColor: state.isSelected
                          ? "#ee3f7c"
                          : state.isFocused
                          ? "var(--gp-option-hover, rgba(0,0,0,0.05))"
                          : "var(--gp-option-bg, white)",
                        color: state.isSelected ? "white" : "var(--gp-option-text, #333)",
                        padding: "12px 16px",
                        fontSize: "14px",
                      }),
                      singleValue: (provided: any) => ({
                        ...provided,
                        fontSize: "14px",
                        color: "var(--gp-text, #495057)",
                      }),
                    },
                  }}
                />

                {!formik.values.venueAddress.name && (
                  <div className="location-help-text">
                    <i className="fa-solid fa-info-circle me-2" />
                    <small className="text-muted">
                      Start typing to search for venues, addresses, or landmarks
                    </small>
                  </div>
                )}
              </div>
              {formik.values.venueAddress.name && (
                <div className="venue-selected-location card">
                  <div className="card-body">
                    <h6>Selected Location</h6>
                    <p>
                      <i className="fa-solid fa-location-dot me-2" />
                      {formik.values.venueAddress.name}
                    </p>
                    {formik.values.venueAddress.addressLine1 && (
                      <p className="text-muted small">
                        <i className="fa-solid fa-map-marker-alt me-2" />
                        {formik.values.venueAddress.addressLine1}
                      </p>
                    )}
                    {formik.values.venueAddress.city && (
                      <p className="mb-0 text-muted small">
                        <i className="fa-solid fa-city me-2" />
                        {formik.values.venueAddress.city}
                        {formik.values.venueAddress.state &&
                          `, ${formik.values.venueAddress.state}`}
                        {formik.values.venueAddress.country &&
                          `, ${formik.values.venueAddress.country}`}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="venue-form-section row">
              <div className="col-md-6">
                <label className="form-label">Capacity</label>
                <input
                  type="text"
                  className="form-control h_50"
                  name="capacity"
                  value={formik.values.capacity}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input
                  className="form-control h_50"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                />
                {formik.errors.phone && formik.submitCount > 0 &&
               (
              <div className="text-danger">{String(formik.errors.phone)}</div>
               )}
              </div>
            </div>

            <div className="venue-form-section row">
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control h_50"
                  name="email"
                  disabled
                  value={formik.values.email}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Website</label>
                <input
                  className="form-control h_50"
                  name="website"
                  value={formik.values.website}
                  onChange={formik.handleChange}
                />
              </div>
            </div>

            <div className="venue-form-section form-group">
              <label className="form-label">About</label>
              <textarea
                className="form-control"
                rows={3}
                name="about"
                value={formik.values.about}
                onChange={formik.handleChange}
              />
            </div>

            <div className="venue-tab-actions">
              <button
                className="btn btn-default btn-hover steps_btn"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? <Spinner size="sm" color="white" /> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VenueDetailsTab;
